'use client';
import * as React from 'react';
import type { MakeOptional } from '@mui/x-internals/types';
import type { ChartPlugin } from '../../models';
import type { DatasetType } from '../../../../models/seriesType/config';
import type { UseChartZAxisSignature } from './useChartZAxis.types';
import type { ZAxisConfig, ZAxisDefaultized } from '../../../../models/z-axis';
import { getColorScale, getOrdinalColorScale } from '../../../colorScale';
import { getSizeScale, getOrdinalSizeScale } from '../../../sizeScale';

function addDefaultId(axisConfig: MakeOptional<ZAxisConfig, 'id'>, defaultId: string): ZAxisConfig {
  if (axisConfig.id !== undefined) {
    return axisConfig as ZAxisConfig;
  }
  return {
    id: defaultId,
    ...axisConfig,
  };
}

function processColorMap(axisConfig: ZAxisConfig) {
  if (!axisConfig.colorMap) {
    return axisConfig;
  }

  return {
    ...axisConfig,
    colorScale:
      axisConfig.colorMap.type === 'ordinal' && axisConfig.data
        ? getOrdinalColorScale({ values: axisConfig.data, ...axisConfig.colorMap })
        : getColorScale(
            axisConfig.colorMap.type === 'continuous'
              ? { min: axisConfig.min, max: axisConfig.max, ...axisConfig.colorMap }
              : axisConfig.colorMap,
          ),
  };
}

function processSizeMap(axisConfig: ZAxisConfig) {
  if (!axisConfig.sizeMap) {
    return axisConfig;
  }

  return {
    ...axisConfig,
    sizeScale:
      axisConfig.sizeMap.type === 'ordinal'
        ? getOrdinalSizeScale({ values: axisConfig.data, ...axisConfig.sizeMap })
        : getSizeScale(
            axisConfig.sizeMap.type === 'continuous'
              ? { min: axisConfig.min, max: axisConfig.max, ...axisConfig.sizeMap }
              : axisConfig.sizeMap,
          ),
  };
}

function getZAxisState(
  zAxis?: readonly MakeOptional<ZAxisConfig, 'id'>[],
  dataset?: Readonly<DatasetType>,
) {
  if (!zAxis || zAxis.length === 0) {
    return { axis: {}, axisIds: [] };
  }

  const zAxisLookup: Record<string, ZAxisDefaultized> = {};
  const axisIds: string[] = [];

  zAxis.forEach((axisConfig, index) => {
    const dataKey = axisConfig.dataKey;
    const defaultizedId = axisConfig.id ?? `defaultized-z-axis-${index}`;
    if (axisConfig.data !== undefined || (dataKey === undefined && !axisConfig.valueGetter)) {
      zAxisLookup[defaultizedId] = processSizeMap(
        processColorMap(addDefaultId(axisConfig, defaultizedId)),
      );
      axisIds.push(defaultizedId);
      return;
    }
    if (dataset === undefined) {
      throw new Error(
        'MUI X Charts: The z-axis uses `dataKey` or `valueGetter` but no `dataset` is provided. ' +
          'When using dataKey or valueGetter, a dataset must be provided to retrieve the axis data. ' +
          'Either provide a dataset prop or use the data property directly on the z-axis.',
      );
    }
    zAxisLookup[defaultizedId] = processSizeMap(
      processColorMap(
        addDefaultId(
          {
            ...axisConfig,
            data: axisConfig.valueGetter
              ? dataset.map((d) => axisConfig.valueGetter!(d))
              : dataset.map((d) => d[dataKey!]),
          },
          defaultizedId,
        ),
      ),
    );
    axisIds.push(defaultizedId);
  });

  return { axis: zAxisLookup, axisIds };
}

export const useChartZAxis: ChartPlugin<UseChartZAxisSignature> = ({ params, store }) => {
  const { zAxis, dataset } = params;

  // The effect do not track any value defined synchronously during the 1st render by hooks called after `useChartZAxis`
  // As a consequence, the state generated by the 1st run of this useEffect will always be equal to the initialization one
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    store.set('zAxis', getZAxisState(zAxis, dataset));
  }, [zAxis, dataset, store]);

  return {};
};

useChartZAxis.params = {
  zAxis: true,
  dataset: true,
};

useChartZAxis.getInitialState = (params) => ({
  zAxis: getZAxisState(params.zAxis, params.dataset),
});
